import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { debounce, noop } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { tableActionsCreator } from "app/actions/tableActions";
import { showSuccessToast } from "app/actions/toast_actions";
import { EAvailabilityMode } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { translate } from "app/filters/i18nFilter";
import type { ActivationObjectSchema } from "app/pages/activation/types/api";
import { updateLastViewedAsset } from "app/pages/digital-suite/home-page/components/CustomAssets/customAssetsApi";
import { AssetType } from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import { KeywordGroupsShareModal } from "app/pages/workspace/marketing/pages/KeywordGroupShareModal";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import { BasicDurations } from "app/services/DurationService";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { allTrackers } from "app/services/track/track";
import type { IAccountUser } from "app/sharing/SharingService";
import { KeywordsGroupUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/KeywordsGroupUtilities";
import { TableSelection } from "pro-features/components/TableSelection/src/TableSelection";
import { TableSelectionGroupKeywordComponent } from "pro-features/components/TableSelection/src/TableSelectionGroupKeywordComponent";
import { ETableSelectionNewGroupDropdownMode } from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import type { RootState } from "single-spa/store/types";

import { useAccountUsersQuery } from "../../../../../../custom-hooks/react-query/useAccountUsersQuery";
import { PageCTAHeader } from "../components/page-header/PageCTAHeader";
import { RecommendationsSidebar } from "../components/recommendations-sidebar/RecommendationsSidebar";
import { ActivationSetCellEnriched } from "../enriched-row-components/activation-set-cell-enriched/ActivationSetCellEnriched";
import { MonitorKeywordsGraph } from "../graphs/monitor-keywords/MonitorKeywordsGraph";
import { useRecommendationsQuery } from "../hooks/useRecommendationsQuery";
import { KeywordsSharingModal } from "../KeywordsSharingModal";
import { useMonitorKeywordsEndpoints } from "../monitorKeywordsEndpoints";
import { useMonitorKeywordsNavigationTargets } from "../monitorKeywordsNavigationTargets";

import { TABLE_SELECTION_KEY } from "./consts";
import { MonitorKeywordsTableTopBar } from "./MonitorKeywordsTableTopBar";
import { MonitorKeywordsGroupTableSettings } from "./MotinorKeywordsGroupTableSettings";
import { MetricContainerStyled, TableWrapper } from "./styledComponents";
import { getApiParamsForTable, getExcelConfig, getListWithoutDuplications, transformData } from "./utils";

interface IMonitorKeywordsPage {
    activationSets: ActivationObjectSchema[];
    isActivationSetError: boolean;
    isActivationSetPlaceholderData: boolean;
    keywordGroupsData: KeywordGroupsResponseLegacy;
    updateKeywordGroup: (group: IUserKeywordGroup) => Promise<IUserKeywordGroup[]>;
}

interface KeywordGroupsResponseLegacy {
    OwnerGroups: IUserKeywordGroup[];
    SharedGroups: IUserKeywordGroup[];
}

export const MonitorKeywordsPage: FC<IMonitorKeywordsPage> = ({
    activationSets,
    isActivationSetError,
    isActivationSetPlaceholderData,
    keywordGroupsData,
    updateKeywordGroup,
}) => {
    const dispatch = useDispatch();
    const { params } = useCurrentStateAndParams();
    const { keywordGroupId, duration = BasicDurations.LAST_THREE_MONTHS, activationSetId, country } = params;
    const endpoints = useMonitorKeywordsEndpoints();
    const navTargets = useMonitorKeywordsNavigationTargets();
    const tableDropdownRef = useRef<TableSelectionGroupKeywordComponent>(null);
    const [isSavingAllRecommendations, setIsSavingAllRecommendations] = useState(false);
    const [singleRecommendationLoadingIndex, setSingleRecommendationLoadingIndex] = useState<number | null>(null);
    const [isShareGroupModalOpen, setIsShareGroupModalOpen] = useState(false);
    const [isRecommendationOpen, setIsRecommendationOpen] = useState(false);
    const [tableSelectionMode, setTableSelectionMode] = useState<ETableSelectionNewGroupDropdownMode>(
        ETableSelectionNewGroupDropdownMode.GROUP_LIST,
    );
    const [sorter, setSorter] = useState<{ sort: string; asc: boolean }>({
        sort: MonitorKeywordsGroupTableSettings.defaultSortField,
        asc: MonitorKeywordsGroupTableSettings.defaultSortDirection === "asc",
    });
    const [keywordGroupConfig, setKeywordGroupConfig] = useState<{
        keywordGroup: IUserKeywordGroup;
        isUsersGroup: boolean;
    }>(() => {
        const usersGroup: IUserKeywordGroup = keywordGroupsData.OwnerGroups.find(
            (group) => group.Id === keywordGroupId,
        );
        const sharedGroup: IUserKeywordGroup = keywordGroupsData.SharedGroups.find(
            (group) => group.Id === keywordGroupId,
        );
        return { keywordGroup: structuredClone(usersGroup || sharedGroup), isUsersGroup: !!usersGroup };
    });
    const selectedRows = useSelector((state: RootState) => getTableSelectedItems(TABLE_SELECTION_KEY)(state));
    const maxKeywordsInGroup: number = KeywordGroupEditorHelpers.getMaxGroupCount();

    const { accountUsers, isAccountUsersLoading } = useAccountUsersQuery();

    useEffect(() => {
        if (!isAccountUsersLoading) {
            updateLastViewedAsset(keywordGroupId, AssetType.KEYWORD_LIST);
        }
    }, [isAccountUsersLoading]);

    const {
        recommendations = [],
        isRecommendationsLoading,
        isRefetching,
        refetchRecommendations,
        updateRecommendations,
    } = useRecommendationsQuery(keywordGroupId, keywordGroupConfig.isUsersGroup);

    const { initialFilters, requestBody } = getApiParamsForTable(activationSets, sorter, {
        ...params,
        groupHash: keywordGroupConfig.keywordGroup.GroupHash,
    });

    const clearAllSelectedRows = () => {
        const tableActions = tableActionsCreator(TABLE_SELECTION_KEY, "Keyword");
        dispatch(tableActions.clearAllSelectedRows());
    };

    const showToast = (text: string) => {
        dispatch(showSuccessToast(text));
    };

    const onSort = ({ field, sortDirection }) => {
        setSorter({
            sort: field,
            asc: sortDirection === "asc",
        });
    };

    const updateGroupAfterAddingKeywords = debounce(
        async (keywords: string[], group: IUserKeywordGroup, isSavingAllRecommendations = false) => {
            try {
                const result = await updateKeywordGroup(group);
                if (result) {
                    const newKeywordsCount = group.Keywords.length - keywordGroupConfig.keywordGroup.Keywords.length;
                    showToast(
                        newKeywordsCount === 1
                            ? translate("workspace.marketing.keywordgroup.recommendation_sidebar.keyword.added", {
                                  keyword: keywords[0],
                              })
                            : translate("workspace.marketing.keywordgroup.recommendation_sidebar.keywords.added", {
                                  numOfKeywords: newKeywordsCount,
                              }),
                    );
                    const indexOfTheCurrentGroup = result.findIndex((x) => x.Id === group.Id);
                    const afterUpdate = result[indexOfTheCurrentGroup];
                    setKeywordGroupConfig({ keywordGroup: afterUpdate, isUsersGroup: keywordGroupConfig.isUsersGroup });
                }
            } catch (_) {
                showToast(translate("workspace.marketing.keywordgroup.recommendation_sidebar.keyword.add.failed"));
            } finally {
                setIsSavingAllRecommendations(false);
                setSingleRecommendationLoadingIndex(null);
                if (isSavingAllRecommendations) {
                    updateRecommendations();
                }
            }
        },
        1000,
    );

    // This callback impacts recommendations changes ie. cached query recommmendations and table top items and right bar items
    const onAddKeywordClick = (keywords: string[], index?: number) => () => {
        const tempGroupForTransaction = { ...keywordGroupConfig.keywordGroup };
        const currentKeywords = tempGroupForTransaction.Keywords;
        const withoutDuplications = getListWithoutDuplications(currentKeywords, keywords);
        const newKeywordsCount = withoutDuplications.length - currentKeywords.length;
        if (newKeywordsCount === 0) {
            showToast(translate("workspace.marketing.keywordgroup.recommendation_sidebar.keyword.exists"));
        } else {
            if (withoutDuplications.length > maxKeywordsInGroup) {
                showToast(translate("workspace.marketing.keywordgroup.recommendation_sidebar.keyword.limit.reached"));
            } else {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const group = { ...tempGroupForTransaction, Keywords: withoutDuplications };
                // if only 1 recommendation was added then remove it from the list
                if (newKeywordsCount === 1) {
                    setSingleRecommendationLoadingIndex(index);
                    updateRecommendations(keywords[0]);
                    // get new recommendations in case last recommendation was added
                    if (!recommendations.length) {
                        void refetchRecommendations();
                        allTrackers.dispatchTrackEvent("Table", "table-results", `suggestions finished/${group.Name}`);
                    }
                    void updateGroupAfterAddingKeywords(keywords, group);
                } else {
                    // all recommendations were added
                    setIsSavingAllRecommendations(true);
                    void updateGroupAfterAddingKeywords(keywords, group, true);
                }
            }
        }
    };

    // This callback impacts keyword lists
    const onAddToDifferentGroup = async ({ id }: { id: string }) => {
        const currentGroup = [...keywordGroupsData.OwnerGroups, ...keywordGroupsData.SharedGroups].find(
            (group) => group.Id === id,
        );
        const currentKeywords = currentGroup.Keywords;
        const newKeywords = selectedRows.map(({ keyword }) => keyword as string);
        const withoutDuplications = getListWithoutDuplications(currentKeywords, newKeywords);
        const newKeywordsCount = withoutDuplications.length - currentKeywords.length;

        if (newKeywordsCount === 0) {
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS);
            tableDropdownRef.current?.openWarning();
        } else if (withoutDuplications.length > maxKeywordsInGroup) {
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
        } else {
            allTrackers.dispatchTrackEvent("Button", "click", "Keyword List Edit/add to another list");
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const group = { ...currentGroup, Keywords: withoutDuplications };
            const result = await updateKeywordGroup(group);
            if (result) {
                clearAllSelectedRows();
                setTableSelectionMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
                tableDropdownRef.current?.close();
                showToast(
                    translate("monitor.keyword.list.page.added.successfully.toast", {
                        count: newKeywordsCount,
                        listName: currentGroup.Name,
                    }),
                );
            }
        }
    };

    // This callback impacts keyword lists
    const onRemoveKeywordsFromList = async () => {
        const selectedRowsKeywords = selectedRows.map(({ keyword }) => keyword as string);
        const currentGroup = keywordGroupsData.OwnerGroups.find((group) => group.Id === keywordGroupId);
        const newKeywords = currentGroup.Keywords.filter((keyword) => !selectedRowsKeywords.includes(keyword)).map(
            (keyword) => keyword.toLowerCase(),
        );
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const group = { ...structuredClone(currentGroup), Keywords: newKeywords };
        try {
            const result = await updateKeywordGroup(group);
            const afterUpdate = result.find((group) => group.Id === keywordGroupId);
            allTrackers.dispatchTrackEvent("Button", "submit-ok", "Keyword List Edit/keyword removed from list");
            showToast(
                translate("monitor.keyword.list.page.removed.successfully.toast", {
                    count: selectedRowsKeywords.length,
                }),
            );
            setKeywordGroupConfig({ keywordGroup: afterUpdate, isUsersGroup: keywordGroupConfig.isUsersGroup });
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
        } catch (error) {
            Sentry.error("Error removing keywords from list", error);
        }
    };

    const groupForShare = keywordGroupsTransforms.adaptGroupDataForShareModal(keywordGroupConfig.keywordGroup, [
        ...keywordGroupsData.OwnerGroups,
        ...keywordGroupsData.SharedGroups,
    ]);

    return (
        <>
            {keywordGroupConfig.isUsersGroup ? (
                <PageCTAHeader
                    users={accountUsers}
                    sharedWithAccounts={keywordGroupConfig.keywordGroup.sharedWithAccounts as IAccountUser[]}
                    sharedWithUsers={keywordGroupConfig.keywordGroup.sharedWithUsers as IAccountUser[]}
                    onShareClick={() => setIsShareGroupModalOpen(true)}
                    keywordGroup={keywordGroupConfig.keywordGroup}
                />
            ) : null}
            <MonitorKeywordsGraph
                keywordGroup={keywordGroupConfig.keywordGroup}
                activationSets={activationSets}
                isActivationSetError={isActivationSetError}
                isActivationSetPlaceholderData={isActivationSetPlaceholderData}
            />
            <MetricContainerStyled>
                <TableWrapper>
                    <SWReactTableWrapperWithSelection
                        tableSelectionKey={TABLE_SELECTION_KEY}
                        tableSelectionProperty="keyword"
                        key={keywordGroupConfig.keywordGroup.GroupHash}
                        serverApi={endpoints.table}
                        tableColumns={MonitorKeywordsGroupTableSettings.getColumns(
                            duration,
                            activationSetId,
                            {
                                field: MonitorKeywordsGroupTableSettings.defaultSortField,
                                sortDirection: MonitorKeywordsGroupTableSettings.defaultSortDirection,
                            },
                            navTargets.keywordAdsState,
                            navTargets.keywordOverviewState,
                        )}
                        requestBody={requestBody}
                        initialFilters={initialFilters}
                        transformData={transformData(country, navTargets.keywordOverviewState)}
                        onSort={onSort}
                        pageIndent={1}
                        tableOptions={{
                            metric: "MarketingWorkspaceKeywordsGroupTable",
                            aboveHeaderComponents: [
                                <TableSelection
                                    key="1"
                                    selectedText={translate("keyword.generator.tool.table.keywords.selected", {
                                        number: selectedRows?.length.toString(),
                                    })}
                                    onCloseClick={clearAllSelectedRows}
                                    addToGroupLabel=""
                                    isVisible={true}
                                    isDisabled={selectedRows?.length === 0}
                                    showSeparator={false}
                                    groupSelectorElement={
                                        <TableSelectionGroupKeywordComponent
                                            ref={(ref) => {
                                                tableDropdownRef.current = ref;
                                            }}
                                            mode={tableSelectionMode}
                                            allItemsExistsMessage="table.selection.keywords.exists"
                                            maxItemsMessage="table.selection.keywords.max.keywords"
                                            key="KeywordSelection"
                                            appendTo=".swReactTable-header-wrapper"
                                            count={selectedRows?.length}
                                            groups={keywordGroupsTransforms.toDropDown(keywordGroupsData.OwnerGroups)}
                                            onCancel={() =>
                                                setTableSelectionMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST)
                                            }
                                            isLoading={false}
                                            hasCreateButton={false}
                                            hasRemoveButton={keywordGroupConfig.isUsersGroup}
                                            onGroupClick={onAddToDifferentGroup}
                                            onRemoveKeywordsFromList={onRemoveKeywordsFromList}
                                            selectedRows={selectedRows}
                                            alertMessage={
                                                selectedRows?.length > maxKeywordsInGroup
                                                    ? translate("table.selection.newgroup.keywords.alert.message", {
                                                          MaxKWs: maxKeywordsInGroup,
                                                      })
                                                    : null
                                            }
                                            shouldAddNewItemToList={false}
                                            groupIconName={KeywordsGroupUtilities.getKeywordsGroupDefaultIcon()}
                                        />
                                    }
                                />,
                            ],
                            shouldEnrichRow: (_, __, e) => e?.currentTarget?.classList?.contains("enrich"),
                            get enrichedRowComponentHeight() {
                                return 300;
                            },
                            enrichedRowComponentProps: {
                                activationSetDomains: activationSets?.find((set) => set.id === activationSetId)
                                    ?.allDomains,
                            },
                            onEnrichedRowClick: noop,
                            shouldApplyEnrichedRowHeightToCell: false,
                            EnrichedRowComponent: ActivationSetCellEnriched,
                            get enrichedRowComponentClass() {
                                return `DropdownContent-container ${
                                    this.props?.selectedRows?.length > 0 ? "selected" : ""
                                }`;
                            },
                        }}
                    >
                        {(topComponentProps) => (
                            <MonitorKeywordsTableTopBar
                                {...topComponentProps}
                                searchPlaceholder="workspaces.marketing.search.placeholder"
                                permissionsComponent={"KeywordAnalysis"}
                                totalTrafficTitle="workspaces.marketing.keywordgroup.totaltraffic.title"
                                tooltipTitle={"workspaces.marketing.totalvisits.tooltip"}
                                onClickRecommended={
                                    keywordGroupConfig.isUsersGroup
                                        ? () => setIsRecommendationOpen((isOpen) => !isOpen)
                                        : noop
                                }
                                onAddKeywordClick={onAddKeywordClick}
                                onRefreshSuggestions={() => void refetchRecommendations()}
                                isRecommendationLoading={isRecommendationsLoading || isRefetching}
                                isRecommendationOpen={isRecommendationOpen}
                                recommendations={recommendations}
                                isUsersGroup={keywordGroupConfig.isUsersGroup}
                                keywordGroup={keywordGroupConfig.keywordGroup}
                                excelConfig={getExcelConfig(
                                    activationSets,
                                    sorter,
                                    params,
                                    topComponentProps?.filtersStateObject?.filter,
                                    endpoints.excel,
                                )}
                            />
                        )}
                    </SWReactTableWrapperWithSelection>
                </TableWrapper>
            </MetricContainerStyled>
            <RecommendationsSidebar
                isRecommendationOpen={isRecommendationOpen}
                isRecommendationLoading={isRecommendationsLoading || isRefetching}
                recommendations={recommendations}
                isSavingAllRecommendations={isSavingAllRecommendations}
                singleRecommendationLoadingIndex={singleRecommendationLoadingIndex}
                onAddKeywordClick={onAddKeywordClick}
                onClickRecommended={() => setIsRecommendationOpen((isOpen) => !isOpen)}
                keywordGroupId={keywordGroupId}
            />
            {swSettings.components.KeywordAnalysis.resources.KeywordListSmartSharingFeature ===
            EAvailabilityMode.HIDDEN ? (
                isShareGroupModalOpen ? (
                    <KeywordGroupsShareModal
                        keywordGroup={groupForShare}
                        isOpen={isShareGroupModalOpen}
                        onCloseClick={() => setIsShareGroupModalOpen(false)}
                        onFinish={() => setIsShareGroupModalOpen(false)}
                    />
                ) : null
            ) : (
                <KeywordsSharingModal
                    isOpen={isShareGroupModalOpen}
                    keywordGroup={groupForShare as IUserKeywordGroup}
                    users={accountUsers}
                    onCloseClick={() => setIsShareGroupModalOpen(false)}
                    onSubmit={() => setIsShareGroupModalOpen(false)}
                />
            )}
        </>
    );
};
